import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Car, Loader2, Plus, Search } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { VehicleAPI, extractError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { VehicleCard } from "@/components/VehicleCard";
import { EmptyState } from "@/components/EmptyState";
import { FUEL_TYPES } from "@/lib/india";
import { toast } from "sonner";

export default function VehiclesPage() {
    const { permissions } = useAuth();
    const canCreate = permissions.includes("vehicle:create");
    const [q, setQ] = useState("");
    const [fuel, setFuel] = useState("");
    const [page, setPage] = useState(1);
    const [data, setData] = useState({ items: [], total: 0 });
    const [loading, setLoading] = useState(true);

    async function load() {
        setLoading(true);
        try {
            const params = { page, page_size: 24 };
            if (q) params.q = q;
            if (fuel) params.fuel_type = fuel;
            setData(await VehicleAPI.list(params));
        } catch (e) {
            toast.error(extractError(e));
        } finally { setLoading(false); }
    }
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [page, fuel]);

    const totalPages = useMemo(() => Math.max(1, Math.ceil(data.total / 24)), [data.total]);

    return (
        <div className="space-y-6 animate-fade-in" data-testid="vehicles-page">
            <header className="flex flex-col items-start justify-between gap-3 sm:flex-row sm:items-end">
                <div>
                    <h1 className="font-display text-3xl font-bold tracking-tight sm:text-4xl">Vehicles</h1>
                    <p className="mt-1 text-sm text-zinc-500">All vehicles across customers in your scope.</p>
                </div>
                {canCreate && (
                    <Link to="/vehicles/new">
                        <Button data-testid="vehicle-create-btn" className="bg-brand-red text-white hover:bg-brand-redDeep">
                            <Plus className="mr-1 h-4 w-4" /> New vehicle
                        </Button>
                    </Link>
                )}
            </header>

            <Card className="overflow-hidden">
                <div className="flex flex-wrap items-center justify-between gap-2 border-b border-zinc-200 p-3 dark:border-zinc-800">
                    <div className="relative">
                        <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-zinc-400" />
                        <Input
                            placeholder="Search registration / VIN / make / model…"
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && (setPage(1), load())}
                            className="w-80 pl-8"
                            data-testid="vehicles-search"
                        />
                    </div>
                    <select value={fuel} onChange={(e) => { setFuel(e.target.value); setPage(1); }} className="h-9 rounded-md border border-input bg-background px-2 text-sm" data-testid="vehicles-fuel">
                        <option value="">All fuels</option>
                        {FUEL_TYPES.map((f) => <option key={f} value={f}>{f}</option>)}
                    </select>
                </div>

                <div className="p-4">
                    {loading ? (
                        <div className="text-center text-sm text-zinc-500"><Loader2 className="mr-2 inline h-4 w-4 animate-spin" /> Loading…</div>
                    ) : data.items.length === 0 ? (
                        <EmptyState
                            icon={Car}
                            title="No vehicles match"
                            description="Try a different filter or add a new vehicle under a customer."
                            action={canCreate && <Link to="/vehicles/new"><Button className="bg-brand-red text-white hover:bg-brand-redDeep">Add vehicle</Button></Link>}
                        />
                    ) : (
                        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
                            {data.items.map((v) => (
                                <div key={v.id}>
                                    <VehicleCard vehicle={v} />
                                    {v.customer && (
                                        <Link to={`/customers/${v.customer.id}`} className="mt-1 block px-1 font-mono text-[10px] uppercase tracking-wider text-zinc-500 hover:underline">
                                            {v.customer.customer_code} · {v.customer.display_name}
                                        </Link>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <div className="flex items-center justify-between border-t border-zinc-200 px-3 py-2 text-xs text-zinc-500 dark:border-zinc-800">
                    <div>{data.total} total · page {page} / {totalPages}</div>
                    <div className="flex gap-1">
                        <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Prev</Button>
                        <Button size="sm" variant="outline" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>Next</Button>
                    </div>
                </div>
            </Card>
        </div>
    );
}
